/**
 * Jyotish Tarang — Pricing Page
 * Tier cards | Razorpay integration | Monthly/Annual toggle
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../App';

declare global {
  interface Window { Razorpay: any; }
}

export default function PricingPage() {
  const { user }  = useAuth();
  const { t } = useTranslation();
  const [annual,  setAnnual]   = useState(false);
  const [loading, setLoading]  = useState<string | null>(null);
  const [sub,     setSub]      = useState<any>(null);
  const [liveConfig, setLiveConfig] = useState<any>(null);
  const [discountCode, setDiscountCode]   = useState('');
  const [discountInfo, setDiscountInfo]   = useState<{ pct: number; message: string } | null>(null);
  const [discountError, setDiscountError] = useState('');
  const [checkingDiscount, setCheckingDiscount] = useState(false);
  // Added 14 Jul 2026 (user-requested feature comparison table): report
  // catalog wasn't previously fetched on this page at all.
  const [reportCatalog, setReportCatalog] = useState<any[]>([]);

  // Fallback defaults — overridden by live config from /api/config/tiers
  // Kept in sync with tier_config's gold_features/platinum_features (v4.2, 14 Jul 2026)
  const DEFAULT_FEATURES_PRO: string[] = t('pricing.defaultFeaturesGold', { returnObjects: true }) as unknown as string[];
  const DEFAULT_FEATURES_EXPERT: string[] = t('pricing.defaultFeaturesPlatinum', { returnObjects: true }) as unknown as string[];
  const FREE_FEATURES: string[] = t('pricing.freeFeatures', { returnObjects: true }) as unknown as string[];
  const GUARANTEES: string[] = t('pricing.guarantees', { returnObjects: true }) as unknown as string[];

  useEffect(() => {
    // Fetch live tier config (prices + features)
    fetch('/api/config/tiers')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setLiveConfig(d); })
      .catch(() => {});
    fetch('/api/reports/catalog')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.reports) setReportCatalog(d.reports); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      axios.get('/api/payments/status')
           .then(r => setSub(r.data.subscription))
           .catch(() => {});
    }
    // Load Razorpay script
    if (!window.Razorpay) {
      const s = document.createElement('script');
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.async = true;
      document.head.appendChild(s);
    }
  }, [user]);

  async function applyDiscount() {
    if (!user) { window.location.href = '/login'; return; }
    const code = discountCode.trim().toUpperCase();
    if (!code) return;
    setCheckingDiscount(true);
    setDiscountError('');
    try {
      const r = await axios.post('/api/discount/validate', { code });
      setDiscountInfo({ pct: r.data.discount_pct, message: r.data.message });
    } catch (e: any) {
      setDiscountInfo(null);
      setDiscountError(e.response?.data?.error || t('pricing.errors.invalidCode'));
    } finally {
      setCheckingDiscount(false);
    }
  }

  async function handleSubscribe(tier: 'pro' | 'expert') {
    if (!user) { window.location.href = '/login'; return; }
    setLoading(tier);
    try {
      const billing = annual ? 'annual' : 'monthly';
      // Map frontend tier names to backend tier names
      const backendTier = tier === 'pro' ? 'gold' : 'platinum';
      const codeToSend = discountInfo ? discountCode.trim().toUpperCase() : undefined;
      const r = await axios.post('/api/payments/order', {
        tier: backendTier, billing, discount_code: codeToSend
      });
      const { subscription_id, amount, key_id, discount_code: appliedCode, discount_pct: appliedPct } = r.data;

      const options = {
        key:          key_id,
        subscription_id,
        currency:     'INR',
        name:         'Jyotish Tarang',
        description:  `${tier.charAt(0).toUpperCase() + tier.slice(1)} — ${billing} (auto-renewing)`,
        image:        '/static/img/logo.png',
        recurring:    true,
        prefill: {
          name:  user.name,
          email: user.email,
        },
        theme: { color: '#FF9933' },
        handler: async (response: any) => {
          try {
            await axios.post('/api/payments/verify', {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_subscription_id: response.razorpay_subscription_id || subscription_id,
              razorpay_signature: response.razorpay_signature,
              tier: backendTier, billing,
              discount_code: appliedCode, discount_pct: appliedPct
            });
            alert(t('pricing.activatedAlert', {
              tier: tier.charAt(0).toUpperCase() + tier.slice(1),
              cycle: billing === 'annual' ? t('pricing.year') : t('pricing.month'),
            }));
            window.location.href = '/dashboard';
          } catch {
            alert(t('pricing.errors.activationFailed'));
          }
        },
        modal: { ondismiss: () => setLoading(null) },
      };
      // Ensure Razorpay is loaded
      if (!window.Razorpay) {
        await new Promise<void>((resolve, reject) => {
          const s = document.createElement('script');
          s.src = 'https://checkout.razorpay.com/v1/checkout.js';
          s.onload = () => resolve();
          s.onerror = () => reject(new Error('Failed to load Razorpay'));
          document.head.appendChild(s);
        });
      }
      const rp = new window.Razorpay(options);
      rp.open();
    } catch (e: any) {
      alert(e.response?.data?.error || t('pricing.errors.setupFailed'));
      setLoading(null);
    }
  }

  const currentTier = sub?.tier || user?.tier || 'free';
  // Prices from live config, fallback to hardcoded
  const goldMonthly  = liveConfig?.gold_price_monthly     ?? 299;
  const goldAnnual   = liveConfig?.gold_price_annual      ?? 2499;
  const platMonthly  = liveConfig?.platinum_price_monthly ?? 599;
  const platAnnual   = liveConfig?.platinum_price_annual  ?? 4999;
  const prices = {
    pro:    annual ? goldAnnual  : goldMonthly,
    expert: annual ? platAnnual  : platMonthly,
  };
  const discounted = (p: number) =>
    discountInfo ? Math.max(1, Math.round(p * (1 - discountInfo.pct / 100))) : p;
  const savings = {
    pro:    Math.round((goldMonthly*12  - goldAnnual)  / (goldMonthly*12)  * 100),
    expert: Math.round((platMonthly*12  - platAnnual)  / (platMonthly*12)  * 100),
  };

  const creditPrice = (liveConfig?.credit_price_paise ?? 9900)/100;
  const extraEach = t('pricing.table.extraEach', { price: creditPrice });
  const featureRows = [
    { name: t('pricing.table.rows.birthCharts'),
      free: t('pricing.table.oneLifetime'),
      gold: t('pricing.table.perMonth', { count: liveConfig?.gold_charts_monthly ?? 1 }),
      plat: t('pricing.table.perMonth', { count: liveConfig?.platinum_charts_monthly ?? 2 }),
      extra: extraEach },
    { name: t('pricing.table.rows.aiQa'),
      free: t('pricing.table.oneLifetime'),
      gold: t('pricing.table.perMonth', { count: liveConfig?.gold_ai_monthly ?? 5 }),
      plat: t('pricing.table.perMonth', { count: liveConfig?.platinum_ai_monthly ?? 10 }),
      extra: extraEach },
    { name: t('pricing.table.rows.milan'),
      free: '✕',
      gold: t('pricing.table.perMonthFree', { count: liveConfig?.gold_milan_monthly ?? 1 }),
      plat: t('pricing.table.perMonthFree', { count: liveConfig?.platinum_milan_monthly ?? 1 }),
      extra: extraEach },
    { name: t('pricing.table.rows.muhurta'),
      free: '✕',
      gold: '✕',
      plat: t('pricing.table.perMonthFree', { count: liveConfig?.platinum_muhurta_monthly ?? 1 }),
      extra: extraEach },
    { name: t('pricing.table.rows.rectification'),
      free: '✕',
      gold: '✕',
      plat: t('pricing.table.perMonthFree', { count: liveConfig?.platinum_rectification_monthly ?? 1 }),
      extra: extraEach },
    { name: t('pricing.table.rows.pdfExport'),
      free: '✕',
      gold: t('pricing.table.perMonth', { count: liveConfig?.gold_pdf_monthly ?? 3 }),
      plat: t('pricing.table.perMonth', { count: liveConfig?.platinum_pdf_monthly ?? 10 }),
      extra: t('pricing.table.resetsNextMonth') },
  ];

  return (
    <div className="page-wrap">
      <div className="container">

        {/* Header */}
        <div style={{ textAlign:'center', marginBottom:32 }}>
          <h2 style={{ fontFamily:'var(--font-display)', color:'var(--indigo)',
                       marginBottom:8, fontSize:'1.8rem' }}>
            {t('pricing.headline')}
          </h2>
          <p style={{ color:'var(--ink2)', maxWidth:480, margin:'0 auto 20px',
                      fontSize:14 }}>
            {t('pricing.subhead')}
          </p>

          {/* Billing toggle */}
          <div style={{ display:'inline-flex', alignItems:'center',
                        background:'var(--cream)', borderRadius:30,
                        border:'0.5px solid var(--parch2)', padding:4 }}>
            <button onClick={() => setAnnual(false)}
                    style={{
                      padding:'6px 20px', borderRadius:24, border:'none',
                      background: !annual ? 'var(--sfb)' : 'transparent',
                      color: !annual ? '#fff' : 'var(--ink2)',
                      cursor:'pointer', fontSize:13, fontWeight:500,
                      transition:'all 0.2s',
                    }}>
              {t('pricing.monthly')}
            </button>
            <button onClick={() => setAnnual(true)}
                    style={{
                      padding:'6px 20px', borderRadius:24, border:'none',
                      background: annual ? 'var(--sfb)' : 'transparent',
                      color: annual ? '#fff' : 'var(--ink2)',
                      cursor:'pointer', fontSize:13, fontWeight:500,
                      transition:'all 0.2s',
                    }}>
              {t('pricing.annual')}
              <span style={{
                fontSize:10, marginLeft:6, background:'var(--teal)', color:'#fff',
                padding:'1px 6px', borderRadius:10,
              }}>
                {t('pricing.savePct', { pct: savings.pro })}
              </span>
            </button>
          </div>
        </div>

        {/* Discount code */}
        <div style={{ textAlign:'center', marginBottom:24 }}>
          <div style={{ display:'inline-flex', gap:10, alignItems:'center',
                        flexWrap:'wrap', justifyContent:'center' }}>
            <span style={{
              fontSize:13, fontWeight:700, color:'var(--sf)',
              textTransform:'uppercase', letterSpacing:1,
              display:'flex', alignItems:'center', gap:6,
            }}>
              🎁 {t('pricing.discountCode')}
            </span>
            <input
              type="text"
              placeholder={t('pricing.enterCode')}
              value={discountCode}
              onChange={e => {
                setDiscountCode(e.target.value.toUpperCase());
                setDiscountInfo(null);
                setDiscountError('');
              }}
              style={{
                padding:'10px 16px', borderRadius:8,
                border:'2px solid var(--sfb)', background:'var(--sfll)',
                fontSize:14, fontFamily:'var(--font-mono)', letterSpacing:2,
                textTransform:'uppercase', width:160, color:'var(--ink)',
                boxShadow:'0 0 0 3px rgba(255,153,51,0.12)',
              }}
            />
            <button
              onClick={applyDiscount}
              disabled={checkingDiscount || !discountCode.trim()}
              className="btn btn-primary"
              style={{ padding:'10px 22px', fontSize:13, fontWeight:600 }}
            >
              {checkingDiscount ? t('pricing.checking') : t('pricing.apply')}
            </button>
          </div>
          {discountInfo && (
            <div style={{ marginTop:8, color:'var(--teal)', fontSize:13, fontWeight:600 }}>
              ✓ {discountInfo.message} — {t('pricing.appliedAtCheckout')}
            </div>
          )}
          {discountError && (
            <div style={{ marginTop:8, color:'var(--red)', fontSize:13 }}>
              {discountError}
            </div>
          )}
        </div>

        {/* Tier cards */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16 }}>

          {/* Free — Darshan */}
          <div className="card" style={{ display:'flex', flexDirection:'column' }}>
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:11, color:'var(--ink3)', textTransform:'uppercase',
                            letterSpacing:1, marginBottom:4 }}>{t('pricing.darshanFreeForever')}</div>
              <h3 style={{ fontFamily:'var(--font-display)', color:'var(--indigo)',
                           fontSize:20, marginBottom:4 }}>{t('pricing.freePlanName')}</h3>
              <div style={{ fontSize:28, fontFamily:'var(--font-mono)',
                            color:'var(--ink)', fontWeight:600 }}>
                ₹0
              </div>
              <div style={{ fontSize:12, color:'var(--ink3)' }}>{t('pricing.alwaysFree')}</div>
            </div>
            <ul style={{ listStyle:'none', flex:1, marginBottom:20 }}>
              {FREE_FEATURES.map(f => (
                <li key={f} style={{ padding:'5px 0', fontSize:13, color:'var(--ink2)',
                                     borderBottom:'0.5px solid var(--parch2)',
                                     display:'flex', gap:8, alignItems:'center' }}>
                  <span style={{ color:'var(--teal)', fontSize:12 }}>✓</span> {f}
                </li>
              ))}
            </ul>
            {currentTier === 'free' ? (
              <div className="btn btn-ghost btn-full" style={{ textAlign:'center',
                cursor:'default', opacity:0.7 }}>
                {t('pricing.currentPlan')}
              </div>
            ) : (
              <a href="/dashboard" className="btn btn-ghost btn-full">
                {t('pricing.goToDashboard')}
              </a>
            )}
          </div>

          {/* Pro */}
          <div style={{ border:'2px solid var(--sfb)', borderRadius:'var(--radius-lg)',
                        background:'var(--sfll)', padding:24, position:'relative',
                        display:'flex', flexDirection:'column' }}>
            <div style={{
              position:'absolute', top:-10, left:'50%', transform:'translateX(-50%)',
              background:'var(--sfb)', color:'#fff', padding:'3px 16px',
              borderRadius:20, fontSize:11, fontWeight:600, whiteSpace:'nowrap',
            }}>
              ⭐ {t('pricing.mostPopular')}
            </div>
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:11, color:'var(--sf)', textTransform:'uppercase',
                            letterSpacing:1, marginBottom:4 }}>{t('pricing.forPractitioners')}</div>
              <h3 style={{ fontFamily:'var(--font-display)', color:'var(--indigo)',
                           fontSize:20, marginBottom:4 }}>{t('tier.gold')}</h3>
              <div>
                <span style={{ fontSize:28, fontFamily:'var(--font-mono)',
                               color:'var(--sfb)', fontWeight:600 }}>
                  ₹{prices.pro.toLocaleString('en-IN')}
                </span>
                <span style={{ fontSize:13, color:'var(--ink3)',
                               marginLeft:6 }}>
                  /{annual ? t('pricing.year') : t('pricing.month')}
                </span>
              </div>
              {annual && (
                <div style={{ fontSize:11, color:'var(--ink3)' }}>
                  <s>₹{(goldMonthly*12).toLocaleString('en-IN')}/{t('pricing.yr')}</s>
                  → {t('pricing.save')} ₹{(goldMonthly*12 - goldAnnual).toLocaleString('en-IN')}
                </div>
              )}
            </div>
            <ul style={{ listStyle:'none', flex:1, marginBottom:20 }}>
              {(liveConfig?.gold_features ?? DEFAULT_FEATURES_PRO).map((f: string) => (
                <li key={f} style={{ padding:'5px 0', fontSize:13, color:'var(--ink)',
                                     borderBottom:'0.5px solid var(--parch2)',
                                     display:'flex', gap:8, alignItems:'flex-start' }}>
                  <span style={{ color:'var(--sfb)', flexShrink:0 }}>✓</span> {f}
                </li>
              ))}
            </ul>
            {currentTier === 'pro' || currentTier === 'gold' ? (
              <div className="btn btn-ghost btn-full" style={{ textAlign:'center',
                cursor:'default', opacity:0.7 }}>
                {t('pricing.currentPlan')}
              </div>
            ) : (
              <button className="btn btn-primary btn-full"
                      onClick={() => handleSubscribe('pro')}
                      disabled={loading === 'pro'}>
                {loading === 'pro'
                  ? t('pricing.settingUp')
                  : discountInfo
                    ? <>{t('pricing.subscribe')} — <s style={{ opacity:0.6 }}>₹{prices.pro.toLocaleString('en-IN')}</s> ₹{discounted(prices.pro).toLocaleString('en-IN')}</>
                    : `${t('pricing.subscribe')} — ₹${prices.pro.toLocaleString('en-IN')}`}
              </button>
            )}
          </div>

          {/* Expert */}
          <div className="card" style={{ display:'flex', flexDirection:'column',
                                         border:'1.5px solid var(--gold)' }}>
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:11, color:'var(--gold)', textTransform:'uppercase',
                            letterSpacing:1, marginBottom:4 }}>{t('pricing.maharishiExpert')}</div>
              <h3 style={{ fontFamily:'var(--font-display)', color:'var(--indigo)',
                           fontSize:20, marginBottom:4 }}>{t('tier.platinum')}</h3>
              <div>
                <span style={{ fontSize:28, fontFamily:'var(--font-mono)',
                               color:'var(--gold)', fontWeight:600 }}>
                  ₹{prices.expert.toLocaleString('en-IN')}
                </span>
                <span style={{ fontSize:13, color:'var(--ink3)', marginLeft:6 }}>
                  /{annual ? t('pricing.year') : t('pricing.month')}
                </span>
              </div>
              {annual && (
                <div style={{ fontSize:11, color:'var(--ink3)' }}>
                  <s>₹{(platMonthly*12).toLocaleString('en-IN')}/{t('pricing.yr')}</s>
                  → {t('pricing.save')} ₹{(platMonthly*12 - platAnnual).toLocaleString('en-IN')}
                </div>
              )}
            </div>
            <ul style={{ listStyle:'none', flex:1, marginBottom:20 }}>
              {(liveConfig?.platinum_features ?? DEFAULT_FEATURES_EXPERT).map((f: string) => (
                <li key={f} style={{ padding:'5px 0', fontSize:13, color:'var(--ink2)',
                                     borderBottom:'0.5px solid var(--parch2)',
                                     display:'flex', gap:8, alignItems:'flex-start' }}>
                  <span style={{ color:'var(--gold)', flexShrink:0 }}>✓</span> {f}
                </li>
              ))}
            </ul>
            {currentTier === 'expert' || currentTier === 'platinum' ? (
              <div className="btn btn-ghost btn-full" style={{ textAlign:'center',
                cursor:'default', opacity:0.7 }}>
                {t('pricing.currentPlan')}
              </div>
            ) : (
              <button className="btn btn-secondary btn-full"
                      style={{ borderColor:'var(--gold)', color:'var(--gold)' }}
                      onClick={() => handleSubscribe('expert')}
                      disabled={loading === 'expert'}>
                {loading === 'expert'
                  ? t('pricing.settingUp')
                  : discountInfo
                    ? <>{t('pricing.subscribe')} — <s style={{ opacity:0.6 }}>₹{prices.expert.toLocaleString('en-IN')}</s> ₹{discounted(prices.expert).toLocaleString('en-IN')}</>
                    : `${t('pricing.subscribe')} — ₹${prices.expert.toLocaleString('en-IN')}`}
              </button>
            )}
          </div>
        </div>

        {/* How pricing works */}
        <div className="card" style={{ marginTop:32, padding:'24px 28px' }}>
          <h3 style={{ fontFamily:'var(--font-display)', color:'var(--indigo)',
                       fontSize:18, marginBottom:6, textAlign:'center' }}>
            {t('pricing.whatsIncluded')}
          </h3>
          <p style={{ fontSize:13, color:'var(--ink3)', textAlign:'center',
                      maxWidth:640, margin:'0 auto 20px' }}>
            {t('pricing.whatsIncludedSub')}
          </p>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ borderBottom:'2px solid var(--parch2)' }}>
                  <th style={{ textAlign:'left', padding:'8px 10px', color:'var(--ink3)',
                               fontSize:11, textTransform:'uppercase', letterSpacing:0.5 }}>{t('pricing.table.feature')}</th>
                  <th style={{ textAlign:'center', padding:'8px 10px', color:'var(--ink3)',
                               fontSize:11, textTransform:'uppercase', letterSpacing:0.5 }}>{t('tier.free')}</th>
                  <th style={{ textAlign:'center', padding:'8px 10px', color:'var(--sfb)',
                               fontSize:11, textTransform:'uppercase', letterSpacing:0.5 }}>{t('tier.gold')}</th>
                  <th style={{ textAlign:'center', padding:'8px 10px', color:'var(--gold)',
                               fontSize:11, textTransform:'uppercase', letterSpacing:0.5 }}>{t('tier.platinum')}</th>
                  <th style={{ textAlign:'center', padding:'8px 10px', color:'var(--teal)',
                               fontSize:11, textTransform:'uppercase', letterSpacing:0.5 }}>{t('pricing.table.beyondLimit')}</th>
                </tr>
              </thead>
              <tbody>
                {featureRows.map(row => (
                  <tr key={row.name} style={{ borderBottom:'0.5px solid var(--parch2)' }}>
                    <td style={{ padding:'8px 10px', color:'var(--ink)' }}>{row.name}</td>
                    <td style={{ padding:'8px 10px', textAlign:'center', color:'var(--ink2)' }}>{row.free}</td>
                    <td style={{ padding:'8px 10px', textAlign:'center', color:'var(--ink2)' }}>{row.gold}</td>
                    <td style={{ padding:'8px 10px', textAlign:'center', color:'var(--ink2)' }}>{row.plat}</td>
                    <td style={{ padding:'8px 10px', textAlign:'center', color:'var(--teal)', fontWeight:600 }}>{row.extra}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 style={{ fontFamily:'var(--font-display)', color:'var(--indigo)',
                       fontSize:18, margin:'32px 0 6px', textAlign:'center' }}>
            {t('pricing.oneTimeReports')}
          </h3>
          <p style={{ fontSize:13, color:'var(--ink3)', textAlign:'center',
                      maxWidth:640, margin:'0 auto 20px' }}>
            {t('pricing.oneTimeReportsSub')}
          </p>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ borderBottom:'2px solid var(--parch2)' }}>
                  <th style={{ textAlign:'left', padding:'8px 10px', color:'var(--ink3)',
                               fontSize:11, textTransform:'uppercase', letterSpacing:0.5 }}>{t('pricing.table.report')}</th>
                  <th style={{ textAlign:'center', padding:'8px 10px', color:'var(--ink3)',
                               fontSize:11, textTransform:'uppercase', letterSpacing:0.5 }}>{t('pricing.table.price')}</th>
                  <th style={{ textAlign:'center', padding:'8px 10px', color:'var(--ink3)',
                               fontSize:11, textTransform:'uppercase', letterSpacing:0.5 }}>{t('pricing.table.pages')}</th>
                  <th style={{ textAlign:'left', padding:'8px 10px', color:'var(--ink3)',
                               fontSize:11, textTransform:'uppercase', letterSpacing:0.5 }}>{t('pricing.table.whatsInside')}</th>
                </tr>
              </thead>
              <tbody>
                {(reportCatalog.length ? [...reportCatalog].sort((a,b) => a.price_inr - b.price_inr) : [
                  { type:'basic', title:'Basic Jyotish Report', price_inr:99, page_count:2,
                    description:'Rasi + Navamsa charts, planetary positions, current + next 2 dashas — no AI narrative.' },
                  { type:'dosha', title:'Dosha Analysis Report', price_inr:399, page_count:8,
                    description:'Mangal, Kaal Sarp, Pitru, Shrapit yoga assessment — severity and remedies.' },
                  { type:'career', title:'Career & Finance Report', price_inr:499, page_count:12,
                    description:'Dashamsha (D-10) analysis, business yogas, 5-year roadmap.' },
                  { type:'comprehensive', title:'Comprehensive Jyotish Report', price_inr:599, page_count:52,
                    description:'Full AI life reading — period-by-period gauges across 15 life domains, plus a technical appendix.' },
                  { type:'varshphal', title:'Annual Varshphal Report', price_inr:699, page_count:10,
                    description:'Solar return for the year ahead — themes, month-by-month highlights, key dates.' },
                ]).map((r: any) => (
                  <tr key={r.type} style={{ borderBottom:'0.5px solid var(--parch2)' }}>
                    <td style={{ padding:'8px 10px', color:'var(--ink)', fontWeight:600 }}>{r.title}</td>
                    <td style={{ padding:'8px 10px', textAlign:'center', color:'var(--teal)', fontWeight:600 }}>₹{r.price_inr}</td>
                    <td style={{ padding:'8px 10px', textAlign:'center', color:'var(--ink2)' }}>{r.page_count}</td>
                    <td style={{ padding:'8px 10px', color:'var(--ink2)' }}>{r.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Guarantees */}
        <div style={{ display:'flex', gap:20, justifyContent:'center',
                      flexWrap:'wrap', marginTop:28, color:'var(--ink2)',
                      fontSize:12 }}>
          {GUARANTEES.map(g => <span key={g}>{g}</span>)}
        </div>

      </div>
    </div>
  );
}
